/**
 * Sidebar (S11 → F4AI-18/S12): navigácia Feed / Ideas / Search + plne
 * prechádzateľný strom topicov z `GET /topics`.
 *
 * S12: strom je **rozbaľovací** — koreňové topicy (30020 s prázdym
 * parentom) sú viditeľné vždy, podtopicy (`a` tag na adresu predka) sa
 * skrywajú za toggle (▸/▾, `aria-expanded`) až po klik. Klik na názov
 * vždy naviguje na `/topics/:slug` (detail topicu). Dôvod: live index má
 * ~115 topicov a plne roztvorený strom bol nečitateľný.
 *
 * Pri chybe alebo prázdnom indexe zobrazíme nenápadnú noticu a stránka
 * ďalej funguje (rovnaké dáva aj `TopicPage`).
 */
import { useState, type JSX } from 'react';
import { NavLink } from 'react-router';

import { useApi } from '../api/ApiContext.js';
import { useResource } from '../hooks/useResource.js';
import { StateLoading } from './StateView.js';
import type { TopicNode } from '../api/types.js';

const NAV_ITEMS = [
  { to: '/', label: 'Feed', icon: '≡', end: true },
  { to: '/ideas', label: 'Ideas', icon: '✦', end: false },
  { to: '/search', label: 'Search', icon: '⌕', end: false },
] as const;

function TopicBranch({ node, depth = 0 }: { node: TopicNode; depth?: number }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <li className="topic-branch" data-depth={depth} data-testid={`topic-branch-${node.slug}`}>
      <div className="topic-branch-row">
        {hasChildren ? (
          <button
            type="button"
            className="topic-toggle"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'zbaliť' : 'rozbaľ'} podtopiky ${node.slug}`}
            data-testid={`topic-toggle-${node.slug}`}
            onClick={() => {
              setExpanded((value) => !value);
            }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="topic-toggle-spacer" aria-hidden="true" />
        )}
        <NavLink to={`/topics/${node.slug}`} className="topic-link" title={node.content || node.slug}>
          {node.slug}
        </NavLink>
        {typeof node.thread_count === 'number' && node.thread_count > 0 && (
          <span
            className="topic-count topic-thread-count"
            data-testid={`topic-threads-${node.slug}`}
            title={`${String(node.thread_count)} nití v topicu a podtopicoch`}
          >
            {String(node.thread_count)}
          </span>
        )}
        {hasChildren && (
          <span className="topic-count" data-testid={`topic-count-${node.slug}`}>
            {String(node.children.length)}
          </span>
        )}
      </div>
      {hasChildren && expanded && (
        <ul className="topic-tree" data-testid={`topic-children-${node.slug}`}>
          {node.children.map((child) => (
            <TopicBranch key={child.address ?? child.slug} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sidebar(): JSX.Element {
  const api = useApi();
  const topics = useResource(() => api.topics(), [api]);

  return (
    <aside className="glass-panel site-sidebar">
      <nav aria-label="hlavná navigácia">
        <p className="side-title">forum</p>
        <div className="side-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `side-link${isActive ? ' active' : ''}`}
            >
              <span className="link-icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div>
        <p className="side-title">
          topiky{topics.data !== null ? ` (${String(topics.data.topic_count)})` : ''}
        </p>
        {topics.loading && <StateLoading label="načítavam strom topicov" />}
        {topics.error !== null && (
          <p className="notice" data-testid="sidebar-topics-error">
            strom topicov nedostupný ({topics.error.code}) — placeholder, skús znova neskôr
          </p>
        )}
        {topics.data !== null && topics.data.topics.length === 0 && (
          <p className="notice" data-testid="sidebar-topics-empty">
            index ešte neobsahuje žiadny topic (kind 30020)
          </p>
        )}
        {topics.data !== null && topics.data.topics.length > 0 && (
          <ul className="topic-tree" data-testid="sidebar-topic-tree">
            {topics.data.topics.map((node) => (
              <TopicBranch key={node.address ?? node.slug} node={node} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
